import clsx from 'clsx';
import ReactGA from 'react-ga4';
import PropTypes from 'prop-types';
import LazyLoad from 'react-lazyload';
import { useRouter } from 'next/router';
import React, { useEffect, useState, useRef } from 'react';
import { Skeleton, Box, Container, Typography, Button } from '@mui/material';
import { PackTypeEnum, PlanEnum, PlanTypeEnum } from 'enums';
import { useStyles } from './styles';
import IndividualPlans from './IndividualPlans';
import TeamsPlans from './TeamsPlans';
import getCleverTap from 'utils/clevertap';
import { useAuthUser } from 'features/auth/AuthUserContext';
import useGeoData from 'hooks/useGeoData';
import {
  getMtgPrimePrices,
  getMtgPricesFromEditage,
  getMtgStudentPricesFromEditage,
  getMtgPublisherSemiAnnualPrices,
} from 'utils/plan';
import { useTranslation } from 'next-i18next';
import PlanFeaturesTable from './PlanFeaturesTable';
import { Icons } from 'components/Icons';
import CustomButton from 'components/CustomButton';
import { COLOR_GRAY_80 } from 'styles/variables/colors';
import DiscountBanner from 'components/DiscountBanner';
import PrimeFeatures from 'components/PrimeFeatures';
import TestimonialCarousel from 'components/TestimonialCarousal';
import ResearchTools from 'components/ResearchTools';
import ProductFeaturesComparison from 'components/ProductFeaturesComparison';
import { geoDiscountConfig } from 'config/discount';
import { useGoogleAnalytics } from 'hooks/useGoogleAnalytics';
import { makeCheckoutUrl } from 'utils';
import { captureUrlParams } from 'utils/urlParams';
import dynamic from 'next/dynamic';
import ProductBenefits from 'components/ProductBenefits';
import PublisherPlan from './PublisherPlan';
import Features from 'components/Features';
import Templates from 'components/Pricing/Templates';

const Props = {
  title: PropTypes.string,
  bgColorProp: PropTypes.string,
  showResearcherPlan: PropTypes.bool,
  showPrimePlan: PropTypes.bool,
};

const DefaultProps = {
  bgColorProp: '#FFF',
  showResearcherPlan: true,
  showPrimePlan: true,
};

function parseCurrency(str) {
  // Remove everything except digits and decimal point
  return Number(str.replace(/[^0-9.]/g, ''));
}

export default function PricingContent({ title, bgColorProp, showResearcherPlan, showPrimePlan }) {
  const classes = useStyles();
  const router = useRouter();
  const clevertap = getCleverTap();
  const { t } = useTranslation('pricing');
  const { gaIdentifiers } = useGoogleAnalytics() || {};
  const utmParams = captureUrlParams();
  const productFeaturesRef = useRef(null);

  const { user, isLoading: isLoadingUser, isAuthenticated } = useAuthUser();
  const isPublisherUser = user?.userType === 'publisher-user' && user?.journalId != null;
  const [publisherPlanData, setPublisherPlanData] = useState(null);
  const [plansType, setPlansType] = useState(PlanTypeEnum.INDIVIDUAL);
  const { geoData, isLoadingGeoData } = useGeoData();
  const [userCountry, setUserCountry] = useState('US');
  const [researcherLifeData, setResearcherLifeData] = useState(null);
  const [mtgStudentPricingData, setMtgStudentPricingData] = useState(null);
  const [mtgPrimeData, setMtgPrimeData] = useState(null);
  const [mtgPrimeDuration, setMtgPrimeDuration] = useState('annual');
  const [researcherDuration, setResearcherDuration] = useState('annual');
  const [packType, setPackType] = useState(false);
  const [isVwoReady, setIsVwoReady] = useState(false);
  const DynamicPrimeSection = dynamic(() => import('../../components/Pricing/PrimeSection'));

  const mtgPrimePlans = {
    ANNUAL: 'paperpal-prime-v2',
    MONTHLY: 'paperpal-prime-monthly-v2',
  };

  const researcherPlans = {
    ANNUAL: 'mtg-researcher-annual',
    // QUARTERLY: 'mtg-researcher-quarterly',
    MONTHLY: 'mtg-researcher-monthly',
  };
  function sendClevertapEvent() {
    clevertap.event.push('Clicks to view information detail', {
      Product: 'MTG',
      Email: user?.email,
      URL: window.location.pathname,
      Description: 'Continue to payment',
      Locale: 'pricing',
    });
  }
  const redirectToCheckout = (selectedPackType) => {
    sendClevertapEvent();

    let packMachineName;
    let data;
    let plan;

    switch (selectedPackType) {
      case PackTypeEnum.MTG_PRIME:
        data = mtgPrimeData;
        plan = mtgPrimeDuration;
        packMachineName = mtgPrimePlans[plan.toUpperCase()];
        break;
      default:
        data = researcherLifeData;
        plan = researcherDuration;
        packMachineName = researcherPlans[plan.toUpperCase()];
    }

    const geoCoupon = geoDiscountConfig[userCountry]?.coupon_code;
    const coupon = geoCoupon || data[plan].coupon_code;

    const planType =
      selectedPackType === PackTypeEnum.STUDENT ? PlanEnum.STUDENT : PlanEnum.RESEARCHER;
    const { client_id, session_id } = gaIdentifiers || {};
    const checkoutUrl = makeCheckoutUrl(planType, userCountry, {
      pack_machine_name: packMachineName,
      ...(coupon && { coupon: coupon }),
      ...(planType === PlanEnum.STUDENT && { pack_type: 'student-plan' }),
      usersource: 'mtg',
      utm_source: utmParams.utm_source || 'mtg',
      utm_campaign: utmParams.utm_campaign || 'subscription',
      utm_medium: utmParams.utm_medium || 'website',
      ...(client_id && { clientId: client_id }),
      ...(session_id && { sessionId: session_id }),
    });
    window.open(checkoutUrl, '_self');
  };
  function sendButtonClickTrackingEvents(plan = null) {
    ReactGA.event({
      category: 'pricing',
      action: 'card_clicked',
      label: 'Click button "Start Now" or "Subscribe" or "Subscribe Now" or "or Subscribe"',
      value: user?.id,
    });

    clevertap.event.push('Clicks to view information detail', {
      Product: 'MTG',
      Email: user?.email,
      URL: router.asPath,
      'Country code': isAuthenticated ? user.country_code : geoData.country_code,
      Description: `Clicked CTA: ${plan}`,
      Locale: 'pricing',
    });
  }

  function handlePlansTypeChange(selectedPlansType) {
    setPlansType(selectedPlansType);
  }

  const scrollToProductFeatures = () => {
    if (productFeaturesRef.current) {
      productFeaturesRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    }
  };

  useEffect(() => {
    let vwoCheckInterval;
    let vwoTimeout;

    const checkVwoReady = () => {
      // Check if VWO has loaded and set the variable
      if (typeof window !== 'undefined' && typeof window.showNewPricingUI !== 'undefined') {
        setIsVwoReady(true);
        if (vwoCheckInterval) clearInterval(vwoCheckInterval);
        if (vwoTimeout) clearTimeout(vwoTimeout);
      }
    };

    // Check immediately
    checkVwoReady();

    // If not ready, check every 100ms
    if (!isVwoReady) {
      vwoCheckInterval = setInterval(checkVwoReady, 100);

      // After 3 seconds, stop waiting and consider VWO as not available
      vwoTimeout = setTimeout(() => {
        setIsVwoReady(true); // Allow component to render with fallback
        // console.log(isVwoReady, '****************');
        if (vwoCheckInterval) clearInterval(vwoCheckInterval);
      }, 3000);
    }

    return () => {
      if (vwoCheckInterval) clearInterval(vwoCheckInterval);
      if (vwoTimeout) clearTimeout(vwoTimeout);
    };
  }, [isVwoReady]);

  useEffect(() => {
    async function fetchResearcherLifeData(country) {
      const researcherPackPrices = await getMtgPricesFromEditage((country || 'US').toLowerCase());
      setResearcherLifeData(researcherPackPrices);

      if (['JP', 'CN'].includes(country)) {
        const studentPackPrices = await getMtgStudentPricesFromEditage(
          (country || 'US').toLowerCase(),
        );
        setMtgStudentPricingData(studentPackPrices);
      }
    }
    async function fetchMtgPrimeData(country) {
      const prices = await getMtgPrimePrices((country || 'US').toLowerCase());
      setMtgPrimeData(prices);
    }

    async function fetchPublisherPlanData(country) {
      try {
        const data = await getMtgPublisherSemiAnnualPrices((country || 'US').toLowerCase());
        setPublisherPlanData(data || false);
      } catch (error) {
        console.error('Error fetching publisher plan data:', error);
        setPublisherPlanData(false);
      }
    }

    if (isLoadingUser || isLoadingGeoData) return;

    const countryCode = isAuthenticated ? user.country_code : geoData.country_code;
    setUserCountry(countryCode);
    if (isPublisherUser) {
      fetchPublisherPlanData(countryCode);
    } else {
      fetchResearcherLifeData(countryCode);
      fetchMtgPrimeData(countryCode);
    }
  }, [isLoadingUser, isLoadingGeoData, geoData?.country_code, user?.country_code, isAuthenticated]);

  // Determine hideFreePlan based on country
  // const hideFreePlan = ['JP', 'CN', 'JPN', 'CHINA'].includes((userCountry || '').toUpperCase());
  const hideFreePlan = false;

  const isDataReady = isPublisherUser
    ? publisherPlanData && isVwoReady
    : researcherLifeData && mtgPrimeData && isVwoReady;

  return isDataReady ? (
    <>
      <Box paddingTop={0} bgcolor={bgColorProp}>
        <LazyLoad>
          <Container>
            {title && (
              <Typography className={classes.title}>
                <b>{title}</b>
              </Typography>
            )}
          </Container>

          {/* {researcherLifeData && researcherLifeData.annual.is_differential && (
            <DiscountBanner showBanner={researcherLifeData.annual.is_differential} />
          )} */}

          {isPublisherUser && publisherPlanData ? (
            <Container>
              <Box pt={3}>
                <PublisherPlan
                  publisherPlanData={publisherPlanData}
                  sendButtonClickTrackingEvents={sendButtonClickTrackingEvents}
                  userCountry={userCountry}
                />
              </Box>
            </Container>
          ) : (
            <Container>
              <Box pt={3} display="flex" justifyContent="center" id="plans-list">
                <Box className={classes.switchBox}>
                  <Button
                    onClick={() => handlePlansTypeChange(PlanTypeEnum.INDIVIDUAL)}
                    className={clsx(
                      classes.switch,
                      plansType === PlanTypeEnum.INDIVIDUAL && [
                        classes.activeSwitch,
                        classes.activeSwitchPink,
                      ],
                    )}
                  >
                    {t('Individual plans')}
                  </Button>

                  <Button
                    onClick={() => handlePlansTypeChange(PlanTypeEnum.TEAMS)}
                    className={clsx(
                      classes.switch,
                      plansType === PlanTypeEnum.TEAMS && [
                        classes.activeSwitch,
                        classes.activeSwitchPink,
                      ],
                    )}
                  >
                    {t('Teams and Institutions')}
                  </Button>
                </Box>
              </Box>
            </Container>
          )}
        </LazyLoad>
      </Box>

      <Box bgcolor={bgColorProp} pt={5} pb={0}>
        <LazyLoad>
          <>
            {!(isPublisherUser && publisherPlanData) && (
              <>
                {/* Individual Plans for new UI */}
                {plansType === PlanTypeEnum.INDIVIDUAL && (
                  <IndividualPlans
                    basePath={router.basePath}
                    pathname={router.pathname}
                    researcherLifeData={researcherLifeData}
                    mtgPrimeData={mtgPrimeData}
                    mtgStudentPricingData={mtgStudentPricingData}
                    sendButtonClickTrackingEvents={sendButtonClickTrackingEvents}
                    userCountry={userCountry}
                    isAuthenticated={isAuthenticated}
                    hideFreePlan={false}
                    showResearcherPlan={true}
                    showPrimePlan={userCountry !== 'CN'}
                    showLabsPlan={false}
                  />
                )}

                {/* Teams Plans for new UI */}
                {plansType === PlanTypeEnum.TEAMS && (
                  <IndividualPlans
                    basePath={router.basePath}
                    pathname={router.pathname}
                    researcherLifeData={researcherLifeData}
                    mtgPrimeData={mtgPrimeData}
                    mtgStudentPricingData={mtgStudentPricingData}
                    sendButtonClickTrackingEvents={sendButtonClickTrackingEvents}
                    userCountry={userCountry}
                    isAuthenticated={isAuthenticated}
                    hideFreePlan={true}
                    showResearcherPlan={false}
                    showPrimePlan={false}
                    showLabsPlan={true}
                  />
                )}

                {/* Prime Section for new UI */}
                {/* {!['CN', 'JP'].includes(userCountry) && plansType === PlanTypeEnum.INDIVIDUAL && (
                  <DynamicPrimeSection userCountry={userCountry} mtgPrimeData={mtgPrimeData} />
                )} */}
              </>
            )}

            <Box
              display="flex"
              justifyContent="center"
              flexWrap="wrap"
              gap={4}
              color={COLOR_GRAY_80}
              mt={4}
            >
              <Box display="flex" alignItems="center" gap={1}>
                <Icons.shieldCheck />
                <Typography fontSize={14}>Pause your subscription</Typography>
              </Box>
              <Box display="flex" alignItems="center" gap={1}>
                <Icons.thumbUpOutlined />
                <Typography fontSize={14}>Cancel anytime</Typography>
              </Box>
              <Box display="flex" alignItems="center" gap={1}>
                <Icons.shieldCheck />
                <Typography fontSize={14}>ISO/IEC 27001:2022 Certified</Typography>
              </Box>
            </Box>

            {/* <Box mt={'60px'}>
              <Typography
                sx={{
                  fontFamily: 'Lato',
                  fontWeight: 700,
                  fontStyle: 'bold',
                  fontSize: '32px',
                  lineHeight: '100%',
                  letterSpacing: '0%',
                  textAlign: 'center',
                  // mt: 6,
                }}
                variant="h3"
              >
                {t('prime_ultimate_solution')}
              </Typography>
              <PrimeFeatures userCountry={userCountry} />
            </Box> */}
            {userCountry !== 'CN' && !(isPublisherUser && publisherPlanData) && (
              <Box key={'product-benefits'}>
                <Typography
                  fontSize={16}
                  sx={{
                    fontFamily: 'Lato',
                    fontWeight: 600,
                    fontStyle: 'normal',
                    fontSize: '16px',
                    lineHeight: '20px',
                    letterSpacing: '0',
                    marginTop: '32px',
                    marginBottom: '24px',
                    textAlign: 'center',
                  }}
                >
                  {t('all_ai_tools_included')}
                </Typography>
                <ProductBenefits defaultClosed singleRow />
              </Box>
            )}
            {!['CN'].includes(userCountry) && !(isPublisherUser && publisherPlanData) && (
              <Box display="flex" justifyContent="center" mt={3}>
                <CustomButton
                  variant="contactMessage"
                  style={{
                    fontFamily: 'Lato',
                    fontWeight: 600,
                    fontStyle: 'SemiBold',
                    fontSize: '16px',
                    lineHeight: '24px',
                    letterSpacing: '0%',
                    textAlign: 'center',
                  }}
                  onClick={scrollToProductFeatures}
                >
                  {t('explore_features_detail')}
                  <Icons.expandMore />
                </CustomButton>
              </Box>
            )}
            {!['JP', 'CN'].includes(userCountry) && !(isPublisherUser && publisherPlanData) && (
              <Box mt={'60px'}>
                <TestimonialCarousel />
              </Box>
            )}
            {/* {!['CN'].includes(userCountry) && (
              <Box mt={'60px'}>
                <ResearchTools
                  currencySymbol={mtgPrimeData['annual'].exchange_currency_symbol}
                  userCountry={userCountry}
                  worthValue={mtgPrimeData['annual']?.formatted_base_amount}
                />
              </Box>
            )} */}
            {!(isPublisherUser && publisherPlanData) && (
              <>
                <Features />
                <Templates />
              </>
            )}

            {!(isPublisherUser && publisherPlanData) && (
              <Box ref={productFeaturesRef}>
                <ProductFeaturesComparison
                  mtgPrimeData={mtgPrimeData}
                  researcherLifeData={researcherLifeData}
                  switcher={{
                    options: [
                      { id: 'annual', name: t('periods.annual') },
                      { id: 'monthly', name: t('periods.monthly') },
                    ],
                    duration: mtgPrimeDuration,
                    setDuration: setMtgPrimeDuration,
                  }}
                  researcherSwitcher={{
                    options: [
                      { id: 'annual', name: t('periods.annual') },
                      // { id: 'quarterly', name: t('periods.quarterly') },
                      { id: 'monthly', name: t('periods.monthly') },
                    ],
                    duration: researcherDuration,
                    setDuration: setResearcherDuration,
                  }}
                  {...(parseCurrency(mtgPrimeData[mtgPrimeDuration].formatted_base_monthly_amount) >
                    parseCurrency(mtgPrimeData[mtgPrimeDuration].formatted_pack_monthly_amount) && {
                    noDiscountPrice: mtgPrimeData[mtgPrimeDuration].formatted_base_monthly_amount,
                  })}
                  {...(parseCurrency(
                    researcherLifeData[researcherDuration].formatted_base_monthly_amount,
                  ) >
                    parseCurrency(
                      researcherLifeData[researcherDuration].formatted_pack_monthly_amount,
                    ) && {
                    researcherNoDiscountPrice:
                      researcherLifeData[researcherDuration].formatted_base_monthly_amount,
                  })}
                  yearlyPricePerMonth={mtgPrimeData[mtgPrimeDuration].formatted_pack_monthly_amount}
                  monthlyPricePerMonth={mtgPrimeData[mtgPrimeDuration].formatted_pack_amount}
                  researcherYearlyPricePerMonth={
                    researcherLifeData[researcherDuration].formatted_pack_monthly_amount
                  }
                  researcherDuration={researcherDuration}
                  description={t('Unlimited access to powerful AI research tools')}
                  duration={mtgPrimeDuration}
                  userCountry={userCountry}
                  isAuthenticated={isAuthenticated}
                  sendButtonClickTrackingEvents={sendButtonClickTrackingEvents}
                  researcherButton={
                    <CustomButton
                      style={{
                        width: '100%',
                        fontFamily: 'Lato',
                        fontWeight: 600,
                        fontStyle: 'SemiBold',
                        fontSize: '16px',
                        lineHeight: '24px',
                        letterSpacing: '0%',
                        textAlign: 'center',
                      }}
                      variant="buttonSecondary"
                      onClick={() => {
                        setPackType(PackTypeEnum.RESEARCHER);
                        redirectToCheckout(PackTypeEnum.RESEARCHER);
                        sendButtonClickTrackingEvents('Get started Researcher Plan');
                      }}
                    >
                      {t('pay')}
                      &nbsp;
                      <span
                        style={{
                          fontFamily: 'Lato',
                          fontWeight: 600,
                          fontStyle: 'normal',
                          fontSize: 16,
                          lineHeight: '24px',
                          letterSpacing: 0,
                          textAlign: 'center',
                          color: '#FFFFFF !important',
                        }}
                      >
                        {researcherLifeData[researcherDuration].formatted_pack_amount}
                      </span>
                      &nbsp;
                      {parseCurrency(researcherLifeData[researcherDuration].formatted_base_amount) >
                        parseCurrency(
                          researcherLifeData[researcherDuration].formatted_pack_amount,
                        ) && (
                        <span
                          style={{
                            fontFamily: 'Lato',
                            fontWeight: 600,
                            fontStyle: 'normal',
                            fontSize: 16,
                            lineHeight: '24px',
                            letterSpacing: 0,
                            textAlign: 'center',
                            textDecoration: 'line-through',
                            color: '#FFFFFF !important',
                          }}
                        >
                          {researcherLifeData[researcherDuration].formatted_base_amount}
                        </span>
                      )}
                    </CustomButton>
                  }
                  button={
                    <CustomButton
                      style={{
                        width: '100%',
                        fontFamily: 'Lato',
                        fontWeight: 600,
                        fontStyle: 'SemiBold',
                        fontSize: '16px',
                        lineHeight: '24px',
                        letterSpacing: '0%',
                        textAlign: 'center',
                      }}
                      variant="buttonSecondary"
                      onClick={() => {
                        setPackType(PackTypeEnum.MTG_PRIME);
                        redirectToCheckout(PackTypeEnum.MTG_PRIME);
                        sendButtonClickTrackingEvents('Get started Paperpal Prime Plan');
                      }}
                    >
                      {t('pay')}
                      &nbsp;
                      <span
                        style={{
                          fontFamily: 'Lato',
                          fontWeight: 600,
                          fontStyle: 'normal', // 'SemiBold' is not a valid CSS value, use fontWeight
                          fontSize: 16,
                          lineHeight: '24px',
                          letterSpacing: 0,
                          textAlign: 'center',
                          color: '#FFFFFF !important',
                        }}
                      >
                        {mtgPrimeData[mtgPrimeDuration].formatted_pack_amount}
                      </span>
                      &nbsp;
                      {parseCurrency(mtgPrimeData[mtgPrimeDuration].formatted_base_amount) >
                        parseCurrency(mtgPrimeData[mtgPrimeDuration].formatted_pack_amount) && (
                        <span
                          style={{
                            fontFamily: 'Lato',
                            fontWeight: 600,
                            fontStyle: 'normal', // 'SemiBold' is not a valid CSS value, use fontWeight
                            fontSize: 16,
                            lineHeight: '24px',
                            letterSpacing: 0,
                            textAlign: 'center',
                            textDecoration: 'line-through',
                            color: '#FFFFFF !important',
                          }}
                        >
                          {mtgPrimeData[mtgPrimeDuration].formatted_base_amount}
                        </span>
                      )}
                    </CustomButton>
                  }
                />
              </Box>
            )}
          </>

          {/* <TeamsPlans
            basePath={router.basePath}
            pathname={router.pathname}
            researcherLifeData={researcherLifeData}
            sendButtonClickTrackingEvents={sendButtonClickTrackingEvents}
          /> */}
        </LazyLoad>
        {/* <PlanFeaturesTable /> */}
      </Box>
    </>
  ) : (
    <Box sx={{ width: '100%', maxWidth: '900px', mx: 'auto', p: 3 }}>
      {/* Tabs Section */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          mb: 4,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            gap: 1,
            p: 0.75,
            bgcolor: 'grey.100',
            borderRadius: '100px',
          }}
        >
          <Skeleton variant="rounded" width={120} height={36} sx={{ borderRadius: '100px' }} />
          <Skeleton variant="rounded" width={160} height={36} sx={{ borderRadius: '100px' }} />
        </Box>
      </Box>

      {/* Pricing Cards Container */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          gap: 3,
          justifyContent: 'center',
          alignItems: 'stretch',
        }}
      >
        {/* Free Plan Card */}
        <Box
          sx={{
            flex: 1,
            maxWidth: 400,
            p: 3,
            border: 1,
            borderColor: 'grey.200',
            borderRadius: 3,
            bgcolor: 'white',
          }}
        >
          {/* Header */}
          <Skeleton variant="text" width={80} height={28} sx={{ mb: 1 }} />
          <Skeleton variant="text" width={100} height={48} sx={{ mb: 2 }} />
          <Skeleton variant="text" width="70%" height={24} sx={{ mb: 3 }} />

          {/* Button */}
          <Skeleton
            variant="rounded"
            width="100%"
            height={44}
            sx={{ borderRadius: '100px', mb: 3 }}
          />

          {/* Divider */}
          <Skeleton variant="text" width="100%" height={1} sx={{ mb: 3 }} />

          {/* Features */}
          <Skeleton variant="text" width={80} height={28} sx={{ mb: 2 }} />
          {[...Array(4)].map((_, i) => (
            <Box key={i} sx={{ display: 'flex', gap: 1.5, mb: 2 }}>
              <Skeleton variant="circular" width={20} height={20} />
              <Skeleton variant="text" width="90%" height={24} />
            </Box>
          ))}
        </Box>

        {/* Researcher Plan Card */}
        <Box
          sx={{
            flex: 1,
            maxWidth: 400,
            p: 3,
            bgcolor: 'white',
            position: 'relative',
          }}
        >
          {/* Header with Popular Badge */}
          <Box
            sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', mb: 1 }}
          >
            <Skeleton variant="text" width={100} height={28} />
            <Skeleton variant="rounded" width={90} height={24} sx={{ borderRadius: '100px' }} />
          </Box>

          {/* Price */}
          <Box sx={{ mb: 1 }}>
            <Skeleton variant="text" width={70} height={24} />
            <Skeleton variant="text" width={130} height={48} />
          </Box>
          <Skeleton variant="text" width="80%" height={24} sx={{ mb: 3 }} />

          {/* Button */}
          <Skeleton
            variant="rounded"
            width="100%"
            height={44}
            sx={{ borderRadius: '100px', mb: 3 }}
          />

          {/* Divider */}
          <Skeleton variant="text" width="100%" height={1} sx={{ mb: 3 }} />

          {/* Features */}
          <Skeleton variant="text" width={80} height={28} sx={{ mb: 2 }} />
          <Skeleton variant="text" width="50%" height={24} sx={{ mb: 2 }} />
          {[...Array(6)].map((_, i) => (
            <Box key={i} sx={{ display: 'flex', gap: 1.5, mb: 2 }}>
              <Skeleton variant="circular" width={20} height={20} />
              <Skeleton variant="text" width="90%" height={24} />
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  );
}

PricingContent.propTypes = Props;
PricingContent.defaultProps = DefaultProps;
